import React, { useState, useEffect } from 'react';
import {
  Globe, Plus, ExternalLink, Edit, Trash2, CheckCircle,
  Building2, Link as LinkIcon, Search, ChevronDown, ChevronRight
} from 'lucide-react';
import axiosInstance from '../../config/axios';
import WebsiteAssetCreation from '../assets/by-type/WebsiteAssetCreation';

interface Website {
  id: number;
  client_id: number;
  client_name: string;
  name: string;
  url: string;
  type: 'website' | 'blog' | 'ecommerce' | 'landing_page' | 'subdomain';
  description?: string;
  platform?: string;
  sitemap_url?: string;
  is_primary: boolean;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export default function WebsiteManagement() {
  const [websites, setWebsites] = useState<Website[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [groupBy, setGroupBy] = useState<'none' | 'client' | 'type'>('none');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadWebsites();
  }, []);

  const loadWebsites = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/websites');
      if (response.data.success) {
        setWebsites(response.data.websites || []);
      }
    } catch (error) {
      console.error('Failed to load websites:', error);
      setWebsites([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const expandAll = () => {
    if (groupBy !== 'none') {
      setExpandedGroups(new Set(Object.keys(groupedWebsites)));
    }
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const filteredWebsites = websites.filter(website => {
    const matchesSearch =
      website.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      website.url?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      website.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      website.platform?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === 'all' || website.type === filterType;

    return matchesSearch && matchesType;
  });

  const groupedWebsites = groupBy === 'none'
    ? { 'All Websites': filteredWebsites }
    : filteredWebsites.reduce((groups: Record<string, Website[]>, website) => {
        const key = groupBy === 'client'
          ? (website.client_name || 'No Client')
          : (website.type || 'No Type');
        if (!groups[key]) groups[key] = [];
        groups[key].push(website);
        return groups;
      }, {});

  const websiteTypes = Array.from(new Set(websites.map(w => w.type).filter(Boolean)));

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      website: 'bg-blue-100 text-blue-800',
      blog: 'bg-purple-100 text-purple-800',
      ecommerce: 'bg-green-100 text-green-800',
      landing_page: 'bg-orange-100 text-orange-800',
      subdomain: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const renderWebsiteRow = (website: Website) => (
    <tr key={website.id} className="hover:bg-gray-50 transition">
      <td className="px-6 py-4">
        <div className="flex items-center">
          <div className="h-10 w-10 flex-shrink-0 bg-blue-100 rounded-lg flex items-center justify-center">
            <Globe className="h-5 w-5 text-blue-600" />
          </div>
          <div className="ml-4">
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium text-gray-900">{website.name}</div>
              {website.is_primary && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Primary
                </span>
              )}
            </div>
            {website.description && (
              <div className="text-sm text-gray-500 line-clamp-1">{website.description}</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <a
          href={website.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <LinkIcon className="h-4 w-4 mr-1" />
          {website.url}
          <ExternalLink className="h-3 w-3 ml-1" />
        </a>
        {website.sitemap_url && (
          <a
            href={website.sitemap_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-xs text-gray-500 hover:text-gray-700 mt-1"
          >
            Sitemap <ExternalLink className="h-3 w-3 ml-1" />
          </a>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center text-sm text-gray-900">
          <Building2 className="h-4 w-4 mr-2 text-gray-400" />
          {website.client_name}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeColor(website.type)}`}>
          {website.type.replace('_', ' ')}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {website.platform || '—'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end space-x-2">
          <a
            href={website.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition"
            title="Visit Website"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
          <button
            className="text-gray-600 hover:text-gray-900 p-1 hover:bg-gray-100 rounded transition"
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Websites</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage client websites and domains
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Website
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search websites..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          >
            <option value="all">All Types</option>
            {websiteTypes.map((type) => (
              <option key={type} value={type}>{type.replace('_', ' ')}</option>
            ))}
          </select>
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          >
            <option value="none">No Grouping</option>
            <option value="client">Group by Client</option>
            <option value="type">Group by Type</option>
          </select>
        </div>

        {/* Expand/Collapse All */}
        {groupBy !== 'none' && Object.keys(groupedWebsites).length > 0 && (
          <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
            <span className="text-sm font-medium text-gray-700">Groups:</span>
            <button
              onClick={expandAll}
              className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition"
            >
              Collapse All
            </button>
          </div>
        )}
      </div>

      {/* Table or Grouped View */}
      {groupBy === 'none' ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Website
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    URL
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Platform
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredWebsites.map(renderWebsiteRow)}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedWebsites).map(([groupName, groupWebsites]) => {
            const isExpanded = expandedGroups.has(groupName);
            return (
              <div key={groupName} className="bg-white rounded-lg shadow overflow-hidden">
                <button
                  onClick={() => toggleGroup(groupName)}
                  className="w-full bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition"
                >
                  <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    {groupName} ({groupWebsites.length})
                  </h3>
                </button>

                {isExpanded && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <tbody className="bg-white divide-y divide-gray-200">
                        {groupWebsites.map(renderWebsiteRow)}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {filteredWebsites.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No websites found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding your first website'}
          </p>
        </div>
      )}

      {/* Create Website Modal - Full Screen */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <div className="min-h-screen">
            {/* Header with Close Button */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-gray-900">Create Website Content</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  loadWebsites(); // Refresh data after creation
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Close
              </button>
            </div>

            {/* Render Website Asset Creation Component */}
            <div className="p-6">
              <WebsiteAssetCreation />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
